import type { TestPlan, PlanContract } from "@/services/testing";

export type PlanExecutionPreviewModel = {
	steps: Array<{
		number: number;
		action: string;
		target: string;
		kind: "setup" | "action" | "check" | "data";
	}>;
	login: string[];
	prerequisites: string[];
	setup: string[];
	pages: string[];
	clicks: string[];
	fields: string[];
	objects: string[];
	records: string[];
	checks: string[];
	cleanup: string[];
	unknowns: string[];
	source: "contract" | "inferred";
};

export function planHealedSpec(plan: TestPlan): string | undefined {
	const steps = plan.steps as unknown;
	if (steps && typeof steps === "object" && !Array.isArray(steps)) {
		const h = (steps as { healed_spec_ts?: unknown }).healed_spec_ts;
		if (typeof h === "string") return h;
	}
	return undefined;
}

export function planExecutionPreview(
	plan: TestPlan,
	contract?: PlanContract,
): PlanExecutionPreviewModel {
	if (contract) return previewFromContract(contract);
	const spec = planSpecText(plan);
	const ir = planIr(plan);
	const irPreview = ir ? previewFromIr(ir) : null;
	const stepLines = irPreview?.steps.length
		? irPreview.steps
		: extractPlanStepLines(spec);
	const steps =
		stepLines.length > 0
			? stepLines
			: fallbackPlanSteps(plan, spec);
	const pages = [
		...(irPreview?.pages ?? []),
		...uniqueMatches(spec, [
			/page\.goto\(\s*["'`]([^"'`]+)["'`]/g,
			/openRecord\([^,]+,\s*[^,]+,\s*["'`]([^"'`]+)["'`]/g,
			/openListView\([^,]+,\s*["'`]([^"'`]+)["'`]/g,
		]),
	];
	const clicks = [
		...(irPreview?.clicks ?? []),
		...uniqueMatches(spec, [
			/getByRole\(\s*["'`](?:button|link|menuitem)["'`]\s*,\s*\{\s*name:\s*["'`]([^"'`]+)["'`]/g,
			/action\([^,]+,\s*["'`]([^"'`]+)["'`]/g,
			/quickAction\([^,]+,\s*["'`]([^"'`]+)["'`]/g,
		]),
	];
	const fields = [
		...(irPreview?.fields ?? []),
		...uniqueMatches(spec, [
			/fillField\([^,]+,\s*["'`]([^"'`]+)["'`]/g,
			/inlineEdit\([^,]+,\s*["'`]([^"'`]+)["'`]/g,
			/assertField\([^,]+,\s*["'`]([^"'`]+)["'`]/g,
			/readField\([^,]+,\s*["'`]([^"'`]+)["'`]/g,
		]),
	];
	const objects = uniqueMatches(spec, [
		/\b(Account|Contact|Lead|Opportunity|Case|Task|Event|User|Profile|PermissionSet|Report|Dashboard|Campaign|Order|Contract|Asset|Product2|Pricebook2|Quote)\b/g,
		/\b([A-Z][A-Za-z0-9_]+__c)\b/g,
	]);
	const records = uniqueMatches(spec, [
		/\b([a-zA-Z0-9]{15,18})\b/g,
		/name\s*:\s*["'`]([^"'`]{3,80})["'`]/g,
		/record(?:Id|Name)?\s*[:=]\s*["'`]([^"'`]{3,80})["'`]/g,
	]).filter((value) => !/^(test|page|expect|record|button|textbox)$/i.test(value));
	const checks = uniqueMatches(spec, [
		/expect\([^)]*\)\.([A-Za-z0-9_]+)/g,
		/test\.step\(["'`]([^"'`]*(?:verify|assert|check|expect)[^"'`]*)["'`]/gi,
	]).slice(0, 6);
	const unknowns = [
		...uniqueMatches(spec, [
			/TODO:\s*([^\n]+)/g,
			/\b(unknown field [^\n]+)/gi,
			/\b(ask user [^\n]+)/gi,
		]),
		...(irPreview?.unknowns ?? []),
	];
	return {
		steps: steps.slice(0, 10),
		login: uniqueStrings([
			...(irPreview?.login ?? []),
			/frontdoor|login|openSalesforce/i.test(spec)
				? "Frontdoor login as the connected Salesforce user"
				: "Uses the connected Salesforce session",
		]).slice(0, 4),
		prerequisites: [],
		setup: [],
		pages: uniqueStrings(pages).slice(0, 8),
		clicks: uniqueStrings(clicks).slice(0, 8),
		fields: uniqueStrings(fields).slice(0, 10),
		objects: uniqueStrings([...(irPreview?.objects ?? []), ...objects]).slice(0, 8),
		records: records.slice(0, 8),
		checks,
		cleanup: [],
		unknowns: uniqueStrings(unknowns).slice(0, 6),
		source: "inferred",
	};
}

export function previewFromContract(contract: PlanContract): PlanExecutionPreviewModel {
	const steps: PlanExecutionPreviewModel["steps"] = [];
	for (const item of contract.setup) {
		steps.push({
			number: steps.length + 1,
			action: item.description,
			target: [item.object, item.ref].filter(Boolean).join(" / "),
			kind: "data",
		});
	}
	for (const item of contract.pages) {
		steps.push({
			number: steps.length + 1,
			action: item.description,
			target: [item.object, item.record, item.name].filter(Boolean).join(" / "),
			kind: "setup",
		});
	}
	for (const item of contract.ui_actions) {
		steps.push({
			number: steps.length + 1,
			action: item.description,
			target: [item.kind, item.label, item.target].filter(Boolean).join(" / "),
			kind: classifyContractAction(item.kind),
		});
	}
	for (const item of contract.assertions) {
		steps.push({
			number: steps.length + 1,
			action: `${item.kind}: ${item.expectation}`,
			target: item.target,
			kind: "check",
		});
	}
	return {
		steps: steps.slice(0, 12).map((step, index) => ({
			...step,
			number: index + 1,
		})),
		login: [`${contract.actor.persona} via connected Salesforce org`],
		prerequisites: contract.prerequisites.slice(0, 8),
		setup: contract.setup.map((item) => item.description).slice(0, 8),
		pages: contract.pages.map((item) => item.description).slice(0, 8),
		clicks: contract.ui_actions
			.map((item) => [item.kind, item.label, item.target].filter(Boolean).join(": "))
			.slice(0, 10),
		fields: contract.fields
			.map((item) =>
				[item.object, item.label, item.operation, item.value]
					.filter(Boolean)
					.join(" / "),
			)
			.slice(0, 12),
		objects: contract.coverage.objects.slice(0, 10),
		records: contract.records
			.map((item) =>
				[item.source, item.object, item.ref, item.soql].filter(Boolean).join(" / "),
			)
			.slice(0, 10),
		checks: contract.assertions
			.map((item) => `${item.target}: ${item.expectation}`)
			.slice(0, 10),
		cleanup: contract.cleanup.map((item) => item.description).slice(0, 8),
		unknowns: contract.clarifications_required.slice(0, 8),
		source: "contract",
	};
}

export function classifyContractAction(
	kind: string,
): PlanExecutionPreviewModel["steps"][number]["kind"] {
	if (/assert|check|verify/i.test(kind)) return "check";
	if (/fill|edit|upload|save/i.test(kind)) return "data";
	if (/navigate|page|window|iframe/i.test(kind)) return "setup";
	return "action";
}

export function planSpecText(plan: TestPlan): string {
	const steps = plan.steps as unknown;
	if (
		steps &&
		typeof steps === "object" &&
		typeof (steps as { spec_ts?: unknown }).spec_ts === "string"
	) {
		return (steps as { spec_ts: string }).spec_ts;
	}
	return typeof steps === "string" ? steps : JSON.stringify(steps ?? {}, null, 2);
}

export function planIr(plan: TestPlan): Record<string, unknown> | null {
	const steps = plan.steps as unknown;
	if (
		steps &&
		typeof steps === "object" &&
		!Array.isArray(steps) &&
		typeof (steps as { ir?: unknown }).ir === "object" &&
		(steps as { ir?: unknown }).ir !== null
	) {
		return (steps as { ir: Record<string, unknown> }).ir;
	}
	return null;
}

export function previewFromIr(ir: Record<string, unknown>): Omit<
	PlanExecutionPreviewModel,
	"records" | "checks" | "cleanup" | "source"
> {
	const rows: PlanExecutionPreviewModel["steps"] = [];
	const objects = new Set<string>();
	const fields = new Set<string>();
	const pages = new Set<string>();
	const clicks = new Set<string>();
	const unknowns = new Set<string>();

	const setup = Array.isArray(ir.setup) ? ir.setup : [];
	for (const op of setup) {
		if (!op || typeof op !== "object") continue;
		const item = op as Record<string, unknown>;
		const objectName = stringValue(item.sobject ?? item.object);
		if (objectName) objects.add(objectName);
		const fieldNames = objectFieldNames(item.fields);
		fieldNames.forEach((field) => fields.add(`${objectName || "record"}.${field}`));
		rows.push({
			number: rows.length + 1,
			action: `${stringValue(item.op) || "Prepare"} ${objectName || "record"} test data`,
			target: fieldNames.length ? fieldNames.join(", ") : objectName,
			kind: "data",
		});
	}

	const steps = Array.isArray(ir.steps) ? ir.steps : [];
	for (const raw of steps) {
		if (!raw || typeof raw !== "object") continue;
		const step = raw as Record<string, unknown>;
		const type = stringValue(step.type) || "step";
		const sobject = stringValue(step.sobject);
		if (sobject) objects.add(sobject);
		const label = stringValue(step.label ?? step.name ?? step.actionName ?? step.text);
		const page = stringValue(step.page);
		if (page || sobject) pages.add([page, sobject].filter(Boolean).join(": "));
		if (["action", "quickAction", "click", "listView"].includes(type) && label) {
			clicks.add(label);
		}
		if (
			["fillField", "assertField", "inlineEdit", "readField", "selectPicklist"].includes(type) &&
			label
		) {
			fields.add(label);
		}
		if (/unknown|todo|ask/i.test(JSON.stringify(step))) {
			unknowns.add(`Needs confirmation for ${type}`);
		}
		rows.push({
			number: rows.length + 1,
			action: irStepAction(type, label),
			target: [sobject, page, label].filter(Boolean).join(" / "),
			kind: classifyPlanStep(type),
		});
	}

	return {
		steps: rows,
		login: ["Frontdoor login as the connected Salesforce user"],
		prerequisites: [],
		setup: rows
			.filter((row) => row.kind === "data")
			.map((row) => row.action),
		pages: [...pages],
		clicks: [...clicks],
		fields: [...fields],
		objects: [...objects],
		unknowns: [...unknowns],
	};
}

export function objectFieldNames(value: unknown): string[] {
	if (!value || typeof value !== "object" || Array.isArray(value)) return [];
	return Object.keys(value as Record<string, unknown>);
}

export function stringValue(value: unknown): string {
	return typeof value === "string" ? cleanPlanText(value) : "";
}

export function irStepAction(type: string, label: string): string {
	if (type === "navigate") return "Open the planned Salesforce page";
	if (type === "fillField") return `Fill field${label ? `: ${label}` : ""}`;
	if (type === "inlineEdit") return `Inline edit${label ? `: ${label}` : ""}`;
	if (type === "quickAction") return `Open quick action${label ? `: ${label}` : ""}`;
	if (type === "action" || type === "click") return `Click${label ? `: ${label}` : ""}`;
	if (type.startsWith("assert")) return `Verify${label ? `: ${label}` : ""}`;
	if (type === "save") return "Save the record";
	return cleanPlanText(type);
}

export function extractPlanStepLines(spec: string): PlanExecutionPreviewModel["steps"] {
	const rows: PlanExecutionPreviewModel["steps"] = [];
	const stepRx = /test\.step\(\s*["'`]([^"'`]+)["'`]/g;
	for (const match of spec.matchAll(stepRx)) {
		const label = cleanPlanText(match[1]);
		rows.push({
			number: rows.length + 1,
			action: label,
			target: inferStepTarget(label),
			kind: classifyPlanStep(label),
		});
	}
	return rows;
}

export function fallbackPlanSteps(
	plan: TestPlan,
	spec: string,
): PlanExecutionPreviewModel["steps"] {
	const rows: PlanExecutionPreviewModel["steps"] = [];
	const legacySteps = Array.isArray(plan.steps) ? plan.steps : [];
	for (const raw of legacySteps) {
		const label =
			typeof raw === "string"
				? raw
				: typeof raw === "object" && raw
					? JSON.stringify(raw)
					: "Execute generated test step";
		rows.push({
			number: rows.length + 1,
			action: cleanPlanText(label),
			target: inferStepTarget(label),
			kind: classifyPlanStep(label),
		});
	}
	if (rows.length > 0) return rows;

	const actionPatterns: Array<[RegExp, string, PlanExecutionPreviewModel["steps"][number]["kind"]]> = [
		[/\.goto\(/, "Open the target Salesforce page", "setup"],
		[/\.getByRole\([^)]*button[^)]*\)\.click\(/i, "Click the planned button", "action"],
		[/\.click\(/, "Click the planned UI control", "action"],
		[/\.fill\(/, "Enter the planned field value", "data"],
		[/\.selectOption\(/, "Select the planned option", "data"],
		[/expect\(/, "Verify the expected result", "check"],
	];
	for (const [rx, label, kind] of actionPatterns) {
		if (rx.test(spec)) {
			rows.push({
				number: rows.length + 1,
				action: label,
				target: "",
				kind,
			});
		}
	}
	if (rows.length === 0) {
		rows.push({
			number: 1,
			action: "Execute the generated Playwright test",
			target: "",
			kind: "action",
		});
	}
	return rows;
}

export function uniqueMatches(spec: string, patterns: RegExp[]): string[] {
	const out = new Set<string>();
	for (const pattern of patterns) {
		for (const match of spec.matchAll(pattern)) {
			const value = cleanPlanText(match[1] ?? match[0]);
			if (value) out.add(value);
		}
	}
	return [...out];
}

export function uniqueStrings(values: string[]): string[] {
	return [...new Set(values.map(cleanPlanText).filter(Boolean))];
}

export function cleanPlanText(value: string): string {
	return value.replace(/\s+/g, " ").trim();
}

export function inferStepTarget(value: string): string {
	const object = uniqueMatches(value, [
		/\b(Account|Contact|Lead|Opportunity|Case|Task|Event|Report|Dashboard|Campaign|Order|Contract|Asset|Product2|Pricebook2|Quote)\b/g,
		/\b([A-Z][A-Za-z0-9_]+__c)\b/g,
	])[0];
	if (object) return object;
	const quoted = value.match(/["'`]([^"'`]{3,80})["'`]/);
	return quoted ? quoted[1] : "";
}

export function classifyPlanStep(
	value: string,
): PlanExecutionPreviewModel["steps"][number]["kind"] {
	const low = value.toLowerCase();
	if (/\b(verify|assert|expect|check|validate|confirm)\b/.test(low)) return "check";
	if (/\b(create|fill|enter|set|insert|update|record)\b/.test(low)) return "data";
	if (/\b(open|login|navigate|setup|load)\b/.test(low)) return "setup";
	return "action";
}
